import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FaCopy, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import PromptGraphPlayground from '../components/ui/PromptGraphPlayground';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { selectAuthUserId } from '../redux/authSlice';
import { selectCourses } from '../redux/coursesSlice';
import {
  getGeneratedPrompt,
  listGeneratedPrompts,
  saveGeneratedPrompt,
} from '../services/supabaseClient/generatedPromptsService';
import { getPromptLinkIds, getPromptTitle, normalizeList } from './promptStudioUtils';
import './PromptGraphPage.css';

const PromptGraphPage = () => {
  const userId = useSelector(selectAuthUserId);
  const courses = useSelector(selectCourses);

  const [savedPrompts, setSavedPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const [linkingPromptId, setLinkingPromptId] = useState('');

  useEffect(() => {
    const loadSavedPrompts = async () => {
      if (!userId) return;
      setLoading(true);
      const { data, error: promptsError } = await listGeneratedPrompts({ userId });
      if (promptsError) {
        setError(promptsError.message || 'Failed to load prompts');
      } else {
        setSavedPrompts(data || []);
        if ((data || []).length > 0) {
          setSelectedPromptId((prev) => prev || data[0].id);
        }
      }
      setLoading(false);
    };

    loadSavedPrompts();
  }, [userId]);

  const promptIndex = useMemo(
    () => new Map(savedPrompts.map((prompt) => [String(prompt.id), prompt])),
    [savedPrompts]
  );

  const filteredPrompts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return savedPrompts;

    return savedPrompts.filter((item) => {
      const haystack = [
        getPromptTitle(item),
        item.input_summary?.goal,
        item.context?.courseTitle,
        item.context?.projectTitle,
        item.type,
        item.generation_mode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [savedPrompts, searchQuery]);

  const graphPrompts = useMemo(
    () =>
      filteredPrompts.map((prompt) => ({
        id: String(prompt.id),
        title: getPromptTitle(prompt),
        type: prompt.type || 'project',
        generationMode: prompt.metadata?.mode || prompt.generation_mode || 'implementation',
        contextLabel: prompt.context?.projectTitle ? 'Project' : 'Course',
        contextValue: prompt.context?.projectTitle || prompt.context?.courseTitle || '--',
        linked: getPromptLinkIds(prompt).length > 0,
      })),
    [filteredPrompts]
  );

  const graphEdges = useMemo(() => {
    const visibleIds = new Set(graphPrompts.map((prompt) => prompt.id));

    return filteredPrompts.flatMap((prompt) =>
      getPromptLinkIds(prompt)
        .map((targetId) => ({
          sourceId: String(prompt.id),
          targetId: String(targetId),
        }))
        .filter((edge) => visibleIds.has(edge.sourceId) && visibleIds.has(edge.targetId))
    );
  }, [filteredPrompts, graphPrompts]);

  const selectedPrompt =
    savedPrompts.find((prompt) => String(prompt.id) === String(selectedPromptId)) || null;

  const selectedPromptWorkflow = useMemo(() => {
    if (!selectedPrompt) return { upstream: [], downstream: [] };

    const upstream = getPromptLinkIds(selectedPrompt)
      .map((id) => promptIndex.get(String(id)) || null)
      .filter(Boolean);

    const downstream = savedPrompts.filter((prompt) =>
      getPromptLinkIds(prompt).includes(String(selectedPrompt.id))
    );

    return { upstream, downstream };
  }, [promptIndex, savedPrompts, selectedPrompt]);

  const refreshSavedPrompts = async () => {
    if (!userId) return [];

    const { data, error: promptsError } = await listGeneratedPrompts({ userId });
    if (promptsError) {
      throw new Error(promptsError.message || 'Failed to load prompts');
    }

    setSavedPrompts(data || []);
    return data || [];
  };

  const persistPromptLinks = async (prompt, nextLinkedPromptIds) => {
    if (!userId || !prompt) return null;

    setLinkingPromptId(prompt.id);

    try {
      const normalizedIds = [...new Set(normalizeList(nextLinkedPromptIds).map(String))];
      const linkedPrompts = normalizedIds
        .map((id) => promptIndex.get(String(id)) || null)
        .filter(Boolean);

      const { data, error: saveError } = await saveGeneratedPrompt({
        id: prompt.id,
        userId,
        type: prompt.type,
        generationMode: prompt.generation_mode,
        context: {
          ...(prompt.context || {}),
          linkedPromptId: normalizedIds[0] || null,
          linkedPromptIds: normalizedIds,
          linkedPromptTitle: linkedPrompts[0] ? getPromptTitle(linkedPrompts[0]) : null,
          linkedPromptTitles: linkedPrompts.map((linkedPrompt) => getPromptTitle(linkedPrompt)),
        },
        inputSummary: prompt.input_summary || {},
        promptAssetKey: prompt.prompt_asset_key || prompt.metadata?.assetKey || null,
        generatedPrompt: prompt.generated_prompt || '',
        llmModel: prompt.llm_model || prompt.metadata?.llmModel || null,
        metadata: prompt.metadata || {},
      });

      if (saveError) {
        throw new Error(saveError.message || 'Failed to update prompt links');
      }

      const refreshed = await refreshSavedPrompts();
      const refreshedPrompt =
        refreshed.find((candidate) => String(candidate.id) === String(prompt.id)) || data || null;

      if (refreshedPrompt) {
        setSelectedPromptId(refreshedPrompt.id);
      }

      return refreshedPrompt;
    } finally {
      setLinkingPromptId('');
    }
  };

  const handleSelectPrompt = async (promptId) => {
    setSelectedPromptId(promptId);
    setError('');
    setCopyFeedback('');

    const existing = savedPrompts.find((item) => String(item.id) === String(promptId));
    if (existing?.generated_prompt) return;

    const { data, error: readError } = await getGeneratedPrompt(promptId);
    if (readError) {
      setError(readError.message || 'Failed to load prompt');
      return;
    }

    setSavedPrompts((prev) =>
      prev.map((item) => (String(item.id) === String(promptId) ? { ...item, ...data } : item))
    );
  };

  const handleCreateGraphEdge = async (sourceId, targetId) => {
    if (!sourceId || !targetId || String(sourceId) === String(targetId)) return;

    const sourcePrompt = promptIndex.get(String(sourceId));
    if (!sourcePrompt) return;

    try {
      setError('');
      await persistPromptLinks(sourcePrompt, [...getPromptLinkIds(sourcePrompt), targetId]);
    } catch (err) {
      setError(err.message || 'Failed to update prompt links');
    }
  };

  const handleRemoveGraphEdge = async (sourceId, targetId) => {
    const sourcePrompt = promptIndex.get(String(sourceId));
    if (!sourcePrompt) return;

    try {
      setError('');
      await persistPromptLinks(
        sourcePrompt,
        getPromptLinkIds(sourcePrompt).filter((linkedId) => String(linkedId) !== String(targetId))
      );
    } catch (err) {
      setError(err.message || 'Failed to update prompt links');
    }
  };

  const handleCopy = async () => {
    if (!selectedPrompt?.generated_prompt) return;

    try {
      await navigator.clipboard.writeText(selectedPrompt.generated_prompt);
      setCopyFeedback('Copied');
    } catch {
      setCopyFeedback('Copy failed');
    }
  };

  if (loading) {
    return (
      <Container className="prompt-graph-page py-4">
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container className="prompt-graph-page dashboard py-4">
      <section className="dashboard-analytics prompt-graph-page__shell">
        <div className="dashboard-analytics__header prompt-graph-page__header">
          <div>
            <span className="dashboard-analytics__eyebrow">Prompt Graph</span>
          </div>

          <div className="prompt-graph-page__actions">
            <Badge bg="dark">Chain Designer</Badge>
            <Button as={Link} to="/prompts" variant="outline-secondary">
              Back to Prompt Studio
            </Button>
          </div>
        </div>

        {error ? (
          <Alert variant="warning" className="mb-4">
            {error}
          </Alert>
        ) : null}

        <div className="prompt-graph-page__toolbar">
          <div className="prompt-graph-page__search">
            <FaSearch className="prompt-graph-page__search-icon" />
            <input
              type="text"
              className="form-control"
              placeholder="Search prompts in the graph..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <Row className="g-4">
          <Col xl={8}>
            <Card className="prompts-page__card">
              <Card.Body>
                {graphPrompts.length > 0 ? (
                  <PromptGraphPlayground
                    prompts={graphPrompts}
                    edges={graphEdges}
                    selectedPromptId={selectedPromptId}
                    onSelectPrompt={handleSelectPrompt}
                    onCreateEdge={handleCreateGraphEdge}
                    onRemoveEdge={handleRemoveGraphEdge}
                    storageKey={userId ? `prompt-graph-layout:${userId}` : 'prompt-graph-layout'}
                  />
                ) : (
                  <div className="dashboard-analytics__state">
                    No prompts match the current search yet.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="prompts-page__card prompts-page__card--output">
              <Card.Body>
                <div className="prompts-page__output-head">
                  <div>
                    <h5 className="mb-1">
                      {selectedPrompt ? getPromptTitle(selectedPrompt) : 'Prompt viewer'}
                    </h5>
                    <p className="text-muted mb-0">
                      Click any node to inspect the full prompt and its nearby workflow.
                    </p>
                  </div>
                  <div className="prompts-page__output-actions">
                    {copyFeedback ? (
                      <span className="prompts-page__copy-feedback">{copyFeedback}</span>
                    ) : null}
                    <Button
                      variant="outline-secondary"
                      onClick={handleCopy}
                      disabled={!selectedPrompt?.generated_prompt}
                    >
                      <FaCopy className="me-2" />
                      Copy
                    </Button>
                  </div>
                </div>

                {selectedPrompt?.generated_prompt ? (
                  <>
                    <pre className="prompts-page__output">{selectedPrompt.generated_prompt}</pre>
                    <div className="prompt-graph-page__summary">
                      <span>
                        Course:{' '}
                        {selectedPrompt.context?.courseTitle ||
                          courses.find(
                            (course) =>
                              String(course.id) === String(selectedPrompt.context?.courseId)
                          )?.title ||
                          '--'}
                      </span>
                      <span>Project: {selectedPrompt.context?.projectTitle || '--'}</span>
                      <span>
                        Mode:{' '}
                        {selectedPrompt.metadata?.mode || selectedPrompt.generation_mode || '--'}
                      </span>
                      {linkingPromptId === selectedPrompt.id ? (
                        <span>Updating links...</span>
                      ) : null}
                    </div>

                    <div className="prompts-studio__workflow-panel">
                      <div className="prompts-studio__workflow-head">
                        <h6 className="mb-0">Connected prompts</h6>
                        <span className="prompts-studio__workflow-summary">
                          {selectedPromptWorkflow.upstream.length} upstream ·{' '}
                          {selectedPromptWorkflow.downstream.length} downstream
                        </span>
                      </div>

                      <div className="prompts-studio__workflow-grid">
                        <div className="prompts-studio__workflow-column">
                          <span className="prompts-studio__workflow-label">Builds on</span>
                          <div className="prompts-studio__workflow-links">
                            {selectedPromptWorkflow.upstream.length > 0 ? (
                              selectedPromptWorkflow.upstream.map((prompt) => (
                                <button
                                  key={`graph-upstream-${prompt.id}`}
                                  type="button"
                                  className="prompts-studio__workflow-node"
                                  onClick={() => handleSelectPrompt(prompt.id)}
                                >
                                  <strong>{getPromptTitle(prompt)}</strong>
                                  <span>{prompt.type || 'prompt'}</span>
                                </button>
                              ))
                            ) : (
                              <span className="prompts-studio__workflow-empty">
                                No upstream prompts linked yet.
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="prompts-studio__workflow-column">
                          <span className="prompts-studio__workflow-label">Feeds into</span>
                          <div className="prompts-studio__workflow-links">
                            {selectedPromptWorkflow.downstream.length > 0 ? (
                              selectedPromptWorkflow.downstream.map((prompt) => (
                                <button
                                  key={`graph-downstream-${prompt.id}`}
                                  type="button"
                                  className="prompts-studio__workflow-node"
                                  onClick={() => handleSelectPrompt(prompt.id)}
                                >
                                  <strong>{getPromptTitle(prompt)}</strong>
                                  <span>{prompt.type || 'prompt'}</span>
                                </button>
                              ))
                            ) : (
                              <span className="prompts-studio__workflow-empty">
                                No downstream prompts connected yet.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="dashboard-analytics__state">
                    Select a node in the graph to inspect the corresponding prompt.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </section>
    </Container>
  );
};

export default PromptGraphPage;
